import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PitchDeckEditor, PitchDeckCreationModal, DeckGallery } from '../components/pitchDeck';
import { supabase, BusinessPlan } from '../lib/supabase';
import { PitchDeck as PitchDeckType } from '../types/pitchDeck';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';

// Create a simple Loader component with size prop support
interface LoaderProps {
  size?: string;
}

const Loader: React.FC<LoaderProps> = ({ size = 'md' }) => {
  // Size classes mapping
  const sizeClasses = {
    'sm': 'h-6 w-6',
    'md': 'h-12 w-12',
    'lg': 'h-16 w-16',
  }[size] || 'h-12 w-12';
  
  return (
    <div className="flex justify-center items-center h-40">
      <div className={`animate-spin rounded-full ${sizeClasses} border-t-2 border-b-2 border-blue-500`}></div>
    </div>
  );
};

const PitchDeck: React.FC = () => {
  const navigate = useNavigate();
  const [showCreationModal, setShowCreationModal] = useState(false);
  const [decks, setDecks] = useState<PitchDeckType[]>([]);
  const [selectedDeck, setSelectedDeck] = useState<PitchDeckType | null>(null);
  const [businessPlans, setBusinessPlans] = useState<BusinessPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSlideId, setSelectedSlideId] = useState<string | null>(null);
  
  
  useEffect(() => {
    // Load decks and business plans on component mount
    loadAllDecks().catch(err => {
      console.error('Failed to load pitch decks:', err);
      setError('Failed to load pitch decks');
    });
    loadBusinessPlans().catch(err => {
      console.error('Failed to load business plans:', err);
      setError('Failed to load business plans');
    });
  }, []);
  
  // Function  // Load business plans for creation modal
  const loadBusinessPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('business_plans')
        .select('*');
        
      if (error) throw error;
      
      setBusinessPlans(data || []);
    } catch (error) {
      console.error('Error loading business plans:', error);
      toast.error('Failed to load business plans');
    }
  };
  
  // Load all pitch decks for the current user
  const loadAllDecks = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('pitch_decks')
        .select('*');
        
      if (error) throw error;
      
      setDecks(data || []);
    } catch (err: any) {
      console.error('Error loading pitch decks:', err);
      setError(err.message || 'Failed to load pitch decks');
      toast.error('Failed to load pitch decks');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle deck selection for editing
  const loadDeck = async (deckId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: deck, error } = await supabase
        .from('pitch_decks')
        .select('*')
        .eq('id', deckId)
        .single();

      if (error) throw error;
      setSelectedDeck(deck);
    } catch (err: any) {
      console.error('Error loading pitch deck:', err);
      setError('Failed to load pitch deck: ' + err.message);
      toast.error('Failed to load pitch deck');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Clear any errors
  const clearErrors = () => {
    setError(null);
  };
  
  // Clear any errors when unmounting
  useEffect(() => {
    return () => clearErrors();
  }, []);
  
  // Handle deck selection
  const handleDeckSelect = (deckId: string) => {
    loadDeck(deckId);
  };
  
  // Handle deck creation
  const handleCreateDeck = () => {
    setShowCreationModal(true);
  };
  
  // Handle view toggle between decks list and editor
  const handleBackToDecks = () => {
    // Reset selected deck and reload all decks to ensure we have latest data
    setSelectedDeck(null);
    loadAllDecks().catch(err => {
      console.error('Failed to reload pitch decks:', err);
      setError('Failed to reload pitch decks');
      toast.error('Failed to reload pitch decks');
    });
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          <strong className="font-bold">Error:</strong>
          <span className="block sm:inline"> {error}</span>
          <button 
            className="absolute top-0 right-0 px-4 py-3"
            onClick={clearErrors}
          >
            <span className="sr-only">Close</span>
            ×
          </button>
        </div>
      )}
      
      {isLoading ? (
        <div className="flex justify-center items-center h-screen">
          <Loader size="large" />
        </div>
      ) : (
        <div className="w-full max-w-6xl mx-auto px-4 py-6">
          {error ? (
            <div className="text-center py-12">
              <p className="text-red-500 mb-4">{error}</p>
              <button
                onClick={handleBackToDecks}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 px-4 rounded-lg"
              >
                Back to Decks
              </button>
            </div>
          ) : selectedDeck ? (
            // Show pitch deck editor when a deck is selected
            <PitchDeckEditor 
              onBackClick={handleBackToDecks}
              deckId={selectedDeck.id}
              initialDeckData={selectedDeck}
              selectedSlideId={selectedSlideId}
              onSlideSelect={setSelectedSlideId}
            />
          ) : (
            <div className="container mx-auto px-4 py-8">
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Your Pitch Decks</h1>
                <button 
                  onClick={handleCreateDeck}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg"
                >
                  Create New Pitch Deck
                </button>
              </div>
              
              {decks.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 mb-4">You don't have any pitch decks yet.</p>
                  <button 
                    onClick={handleCreateDeck}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg"
                  >
                    Create Your First Pitch Deck
                  </button>
                </div>
              ) : (
                <DeckGallery 
                  decks={decks} 
                  onSelectDeck={handleDeckSelect} 
                />
              )}
            </div>
          )}
        </div>
      )}
      
      {showCreationModal && (
        <PitchDeckCreationModal 
          onClose={() => setShowCreationModal(false)} 
          businessPlans={businessPlans}
          onDeckCreated={(deckId: string) => {
            setShowCreationModal(false);
            loadDeck(deckId);
          }}
          isGenerating={loading}
          createNewDeck={async (title: string) => {
            try {
              const { data: deck, error } = await supabase
                .from('pitch_decks')
                .insert({
                  title,
                  user_id: (await supabase.auth.getUser()).data.user?.id,
                  last_edited: new Date().toISOString(),
                  theme: {
                    primaryColor: '#3B82F6',
                    secondaryColor: '#DBEAFE',
                    fontFamily: 'Inter',
                    logoUrl: ''
                  },
                  slides: [
                    {
                      id: uuidv4(),
                      title: 'Title Slide',
                      type: 'title',
                      content: '# ' + title + '\n\nYour Company Name\nDate',
                      order: 0
                    },
                    {
                      id: uuidv4(),
                      title: 'Problem Statement',
                      type: 'content',
                      content: '# Problem Statement\n\n- What problem are you solving?\n- Who has this problem?\n- How big is this problem?',
                      order: 1
                    },
                    {
                      id: uuidv4(),
                      title: 'Solution',
                      type: 'content',
                      content: '# Solution\n\n- What is your solution?\n- How does it work?\n- Why is it better than alternatives?',
                      order: 2
                    }
                  ]
                })
                .select()
                .single();

              if (error) throw error;
              return deck.id;
            } catch (error) {
              console.error('Error creating new deck:', error);
              toast.error('Failed to create new deck');
              throw error;
            }
          }}
          generateDeckFromBusinessPlan={async (businessPlanId: string) => {
            try {
              // Get the business plan
              const { data: businessPlan, error: bpError } = await supabase
                .from('business_plans')
                .select('*')
                .eq('id', businessPlanId)
                .single();

              if (bpError) throw bpError;
              
              // Create a new deck with slides based on the business plan
              const { data: deck, error } = await supabase
                .from('pitch_decks')
                .insert({
                  title: businessPlan.name + ' Pitch Deck',
                  user_id: (await supabase.auth.getUser()).data.user?.id,
                  last_edited: new Date().toISOString(),
                  business_plan_id: businessPlanId,
                  theme: {
                    primaryColor: '#3B82F6',
                    secondaryColor: '#DBEAFE',
                    fontFamily: 'Inter',
                    logoUrl: ''
                  },
                  slides: [
                    {
                      id: uuidv4(),
                      title: 'Title Slide',
                      type: 'title',
                      content: '# ' + businessPlan.name + '\n\nYour Company Name\nDate',
                      order: 0
                    },
                    {
                      id: uuidv4(),
                      title: 'Problem Statement',
                      type: 'content',
                      content: '# Problem Statement\n\n' + businessPlan.problem_statement,
                      order: 1
                    },
                    {
                      id: uuidv4(),
                      title: 'Solution',
                      type: 'content',
                      content: '# Solution\n\n' + businessPlan.solution_description,
                      order: 2
                    },
                    {
                      id: uuidv4(),
                      title: 'Market Analysis',
                      type: 'content',
                      content: '# Market Analysis\n\n' + businessPlan.market_analysis,
                      order: 3
                    },
                    {
                      id: uuidv4(),
                      title: 'Business Model',
                      type: 'content',
                      content: '# Business Model\n\n' + businessPlan.business_model,
                      order: 4
                    }
                  ]
                })
                .select()
                .single();

              if (error) throw error;
              return deck.id;
            } catch (error) {
              console.error('Error generating deck from business plan:', error);
              toast.error('Failed to generate deck from business plan');
              throw error;
            }
          }}
          generateDeckFromScratch={async (options) => {
            try {
              // In a real implementation, we would call an AI service here
              toast.info('Generating pitch deck content with AI...');
              
              // Simulated AI-generated content
              await new Promise(resolve => setTimeout(resolve, 2000));
              
              const { data: deck, error } = await supabase
                .from('pitch_decks')
                .insert({
                  title: options.title,
                  user_id: (await supabase.auth.getUser()).data.user?.id,
                  last_edited: new Date().toISOString(),
                  theme: {
                    primaryColor: '#3B82F6',
                    secondaryColor: '#DBEAFE',
                    fontFamily: 'Inter',
                    logoUrl: ''
                  },
                  slides: [
                    {
                      id: uuidv4(),
                      title: 'Title Slide',
                      type: 'title',
                      content: '# ' + options.title + '\n\nYour Company Name\nDate',
                      order: 0
                    },
                    {
                      id: uuidv4(),
                      title: 'Problem Statement',
                      type: 'content',
                      content: '# Problem Statement\n\n- Problem 1 related to ' + options.industry + '\n- Problem 2 affecting ' + options.targetAudience + '\n- Market gap analysis',
                      order: 1
                    },
                    {
                      id: uuidv4(),
                      title: 'Solution',
                      type: 'content',
                      content: '# Solution\n\n' + (options.keyPoints?.map(point => '- ' + point).join('\n') || '- Key solution point'),
                      order: 2
                    }
                  ]
                })
                .select()
                .single();

              if (error) throw error;
              return deck.id;
            } catch (error) {
              console.error('Error generating AI deck:', error);
              toast.error('Failed to generate deck with AI');
              throw error;
            }
          }}
        />
      )}
    </div>
  );
};

export default PitchDeck;
